import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import MaterialSelect from '@material-ui/core/Select';
import MaterialFormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import classnames from 'classnames';
import styles from './styles.module.scss';
import useKeyDownAutoSelect from './useKeyDownAutoSelect';

/**
 * Another select component, with un-selectable placeholder.
 */
export const Select = forwardRef(
    (
        {
            helperText,
            currentValue,
            children,
            canSelectPlaceholder,
            placeholderValue,
            options,
            className,
            ...props
        },
        ref
    ) => {
        // using currentValue instead of value or defaultValue, as those have specific value
        // to their usage as controlled or uncontrolled inputs. And for this condition to work,
        // we need to be aware of the current value -- and we want to leave the decision to the user
        // on whether or not the component is controlled or not.
        const selectClassNames = classnames(styles.root, className, {
            [styles.placeholder]: currentValue === placeholderValue // isPlaceholderTheCurrentValue
        });

        const { onKeyDown, addAction, onOpen } = useKeyDownAutoSelect();

        return (
            <MaterialFormControl fullWidth>
                <MaterialSelect
                    variant="outlined"
                    fullWidth
                    ref={ref}
                    className={selectClassNames}
                    {...props}
                    onOpen={onOpen}
                >
                    {options.map(({ text, value: v }) => {
                        const valueIsPlaceholder = v === placeholderValue;
                        const isPlaceHolder =
                            valueIsPlaceholder && !canSelectPlaceholder;

                        return (
                            <MenuItem
                                key={v}
                                value={v}
                                className={classnames({
                                    [styles.placeholderOption]: valueIsPlaceholder
                                })}
                                disableRipple={isPlaceHolder}
                                disabled={isPlaceHolder}
                                onKeyDown={onKeyDown}
                                action={actions => {
                                    addAction({
                                        text,
                                        value: v,
                                        actions
                                    });
                                }}
                            >
                                {text}
                            </MenuItem>
                        );
                    })}
                    {children}
                </MaterialSelect>
                {helperText && (
                    <FormHelperText variant="outlined" error={props.error}>
                        {helperText}
                    </FormHelperText>
                )}
            </MaterialFormControl>
        );
    }
);

Select.displayName = 'NeptuneDropdown';

Select.propTypes = {
    ...MaterialSelect.propTypes,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.any,
            value: PropTypes.any
        })
    ),
    placeholderValue: PropTypes.any,
    currentValue: PropTypes.any,
    canSelectPlaceholder: PropTypes.bool
};

Select.defaultProps = {
    ...MaterialSelect.defaultProps,
    options: [],
    placeholderValue: null,
    currentValue: null,
    canSelectPlaceholder: false
};
